import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TextBlockModalData } from '../../models/modal-data.model';
import { ITextBlock, TextBlockType } from '../../models/text-block.model';

@Component({
  selector: 'app-text-block-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectModule],
  templateUrl: './text-block-modal.component.html',
  styleUrls: ['./text-block-modal.component.scss'],
})
export class TextBlockModalComponent implements OnInit {
  public textBlockForm!: FormGroup;
  public isEditMode = false;
  public isSubmitting = false;

  public readonly typeOptions = [
    { value: TextBlockType.LZ, label: 'Lieferschein (LZ)' },
    { value: TextBlockType.ABS, label: 'Auftragsbegleitschein (ABS)' },
  ];

  constructor(
    private fb: FormBuilder,
    @Inject('MODAL_DATA') private data: TextBlockModalData,
    @Inject('MODAL_CLOSE') private modalClose: (result: ITextBlock | null) => void
  ) { }

  ngOnInit(): void {
    this.isEditMode = this.data?.mode === 'edit';
    this.initForm();

    if (this.isEditMode && this.data?.textBlock) {
      this.patchFormValues(this.data.textBlock);
    }
  }

  private initForm(): void {
    this.textBlockForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(2)]],
      description: ['', [Validators.required, Validators.minLength(5)]],
      type: [null, [Validators.required]],
    });
  }

  private patchFormValues(textBlock: ITextBlock): void {
    this.textBlockForm.patchValue({
      title: textBlock.title,
      description: textBlock.description,
      type: textBlock.type ?? null,
    });
  }

  public get title(): string {
    return this.isEditMode ? 'Textbaustein bearbeiten' : 'Textbaustein hinzufügen';
  }

  public close(): void {
    this.modalClose(null);
  }

  @HostListener('document:keydown.escape', ['$event'])
  public handleEscape(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    this.close()
  }

  public submit(): void {
    if (this.textBlockForm.invalid) {
      this.textBlockForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValue = this.textBlockForm.value;

    const textBlock: ITextBlock = {
      title: formValue.title,
      description: formValue.description,
      type: formValue.type,
    };

    this.modalClose(textBlock);
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.textBlockForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  public getFieldError(fieldName: string): string {
    const field = this.textBlockForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'Dieses Feld ist erforderlich';
    if (field.errors['minlength']) return `Mindestens ${field.errors['minlength'].requiredLength} Zeichen`;

    return '';
  }
}
